// Prompt-var form state (#52) — the fields half of <PromptVarFormModal/>, split
// out for the same reason `template-form.ts` is: the address rule, the JSON parse
// and the "may this submit" rule are state, not rendering.
//
// The value is arbitrary JSON on the wire (`config set` takes any JSON: scalar,
// array, object, null), so the box holds JSON text and this parses it. A bare
// word is not JSON, and quietly quoting it for the caller would mean the UI
// stores `"true"` where the MCP verb stores `true` — the two doors would disagree
// about the same key. So the box refuses, naming what it wants.
//
// The scope/ref pairing is the DOMAIN's rule (`promptVarScopeNeedsRef`), read
// from the same helper the API resolves the address with — not a second copy.
// The credential-shape refusal stays entirely server-side: one detector, both
// doors, and its 422 renders inline.

import { type PromptVarScope, promptVarScopeNeedsRef } from '@developerz/domain';
import { batch, createSignal } from 'solid-js';
import type { ApiPromptVar, PromptVarAddress } from './prompt-vars';

export type PromptVarFormController = {
  readonly scope: () => PromptVarScope;
  readonly setScope: (v: PromptVarScope) => void;
  readonly ref: () => string;
  readonly setRef: (v: string) => void;
  readonly key: () => string;
  readonly setKey: (v: string) => void;
  readonly value: () => string;
  readonly setValue: (v: string) => void;
  /** Does this scope require a ref? `account` refuses one; the others demand it. */
  readonly needsRef: () => boolean;
  /** Non-null when the value box holds something that is not JSON. */
  readonly valueError: () => string | null;
  /** May this form submit? Key present, ref paired with scope, value parseable. */
  readonly valid: () => boolean;
  /** The address this form writes to — `ref` null for account scope. */
  readonly address: () => PromptVarAddress;
  /** The parsed value to send. Only meaningful while `valid()`. */
  readonly parsedValue: () => unknown;
  /**
   * (Re)seed every field from a var, or empty for a create. Called when the modal
   * opens on a different row — it stays mounted so its exit animation can play,
   * so seeding is what makes "edit var B" show B rather than A.
   *
   * Scope/ref/key stay EDITABLE on an edit: they ARE the address, so changing
   * them writes a different row rather than moving this one — exactly what
   * `config set` does, and the row it came from is deleted separately.
   */
  readonly seed: (initial: ApiPromptVar | null) => void;
};

/** `invalidJson` is the copy shown when the value box does not parse. */
export function createPromptVarForm(invalidJson: string): PromptVarFormController {
  const [scope, setScope] = createSignal<PromptVarScope>('account');
  const [ref, setRef] = createSignal('');
  const [key, setKey] = createSignal('');
  const [value, setValue] = createSignal('');

  const parsed = (): { ok: true; value: unknown } | { ok: false } => {
    // An empty box is NOT `null`: `null` is a value a caller sets deliberately
    // (the schema says so), and inferring it from an empty textarea would make
    // "I cleared the box" and "I meant JSON null" the same gesture.
    if (value().trim() === '') return { ok: false };
    try {
      return { ok: true, value: JSON.parse(value()) as unknown };
    } catch {
      return { ok: false };
    }
  };

  const needsRef = (): boolean => promptVarScopeNeedsRef(scope());

  return {
    scope,
    setScope,
    ref,
    setRef,
    key,
    setKey,
    value,
    setValue,
    needsRef,
    // An EMPTY box is not an error, it is an unfinished form — `valid()` already
    // holds the submit. Only text that is present and unparseable is refused.
    valueError: () => (value().trim() === '' || parsed().ok ? null : invalidJson),
    valid: () => key().trim() !== '' && (!needsRef() || ref().trim() !== '') && parsed().ok,
    address: () => ({
      scope: scope(),
      // Account scope takes NO ref — the API 400s one, so nothing is sent even
      // if a ref was typed before the scope was switched back.
      ref: needsRef() ? ref().trim() : null,
      key: key().trim(),
    }),
    parsedValue: () => {
      const p = parsed();
      return p.ok ? p.value : null;
    },
    seed: (initial) =>
      batch(() => {
        setScope(initial?.scope ?? 'account');
        setRef(initial?.ref ?? '');
        setKey(initial?.key ?? '');
        setValue(initial === null ? '' : JSON.stringify(initial.value, null, 2));
      }),
  };
}
